import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '../firebase';

const IMAGE_FILE_EXTENSIONS = /\.(png|jpe?g|webp|gif|bmp|avif|heic|heif|svg|ico)$/i;
export const IMAGE_UPLOAD_ACCEPT = 'image/*,.heic,.heif';
export const ATTACHMENT_UPLOAD_ACCEPT = `${IMAGE_UPLOAD_ACCEPT},application/pdf,.doc,.docx`;
export const MESSAGE_ATTACHMENT_UPLOAD_ACCEPT = `${ATTACHMENT_UPLOAD_ACCEPT},.xls,.xlsx,.ppt,.pptx`;

const mimeTypeFromName = (fileName = '') => {
  if (/\.png$/i.test(fileName)) return 'image/png';
  if (/\.webp$/i.test(fileName)) return 'image/webp';
  if (/\.gif$/i.test(fileName)) return 'image/gif';
  if (/\.bmp$/i.test(fileName)) return 'image/bmp';
  if (/\.avif$/i.test(fileName)) return 'image/avif';
  if (/\.svg$/i.test(fileName)) return 'image/svg+xml';
  if (/\.ico$/i.test(fileName)) return 'image/x-icon';
  if (/\.heic$/i.test(fileName)) return 'image/heic';
  if (/\.heif$/i.test(fileName)) return 'image/heif';
  if (/\.jpe?g$/i.test(fileName)) return 'image/jpeg';
  return '';
};

const isHeicFile = (file) => (
  file?.type === 'image/heic'
  || file?.type === 'image/heif'
  || /\.heic$/i.test(file?.name || '')
  || /\.heif$/i.test(file?.name || '')
);

export const isSupportedImageFile = (file) => (
  Boolean(file)
  && (
    file.type?.startsWith('image/')
    || IMAGE_FILE_EXTENSIONS.test(file.name || '')
  )
);

export const normalizeImageFile = async (file) => {
  if (!file) {
    throw new Error('Choose an image to upload');
  }

  if (!isSupportedImageFile(file)) {
    throw new Error('Choose a photo or image file');
  }

  if (isHeicFile(file)) {
    const { default: heic2any } = await import('heic2any');
    const jpegBlob = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
    const converted = Array.isArray(jpegBlob) ? jpegBlob[0] : jpegBlob;
    const convertedName = (file.name || 'photo.heic')
      .replace(/\.heic$/i, '.jpg')
      .replace(/\.heif$/i, '.jpg');
    return new File([converted], convertedName, { type: 'image/jpeg' });
  }

  if (!file.type) {
    const inferredType = mimeTypeFromName(file.name);
    if (inferredType) {
      return new File([file], file.name || 'photo', { type: inferredType });
    }
  }

  return file;
};

/**
 * Upload a photo to Firebase Storage
 * @param {File|Blob} file - The image file to upload
 * @param {string} schoolId - School ID for storage path
 * @param {string} entityType - Type of entity (e.g., 'students', 'staff')
 * @param {string} entityId - Entity ID for storage path
 * @returns {Promise<string>} The download URL of the uploaded photo
 */
export const uploadPhoto = async (file, schoolId, entityType, entityId) => {
  if (!file || !schoolId || !entityType || !entityId) {
    throw new Error('Missing required parameters for photo upload');
  }

  try {
    // Generate a unique filename with timestamp to avoid caching issues
    const timestamp = Date.now();
    const extension = file.name?.split('.').pop() || 'jpg';
    const fileName = `${entityId}_${timestamp}.${extension}`;

    // Create storage reference: schools/{schoolId}/photos/{entityType}/{entityId}/{fileName}
    const storageRef = ref(storage, `schools/${schoolId}/photos/${entityType}/${entityId}/${fileName}`);

    // Upload the file
    const snapshot = await uploadBytes(storageRef, file);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('[PhotoStorage] Upload failed:', error);
    throw error;
  }
};

/**
 * Convert a Blob to a File object
 * @param {Blob} blob - The blob to convert
 * @param {string} fileName - The file name
 * @returns {File} The File object
 */
export const blobToFile = (blob, fileName = 'photo.jpg') => {
  return new File([blob], fileName, { type: blob.type || 'image/jpeg' });
};

/**
 * Upload a blob to Firebase Storage with custom path
 * @param {Blob} blob - The blob to upload
 * @param {string} path - Full storage path (e.g., 'stickers/123456.png')
 * @param {string} contentType - MIME type (e.g., 'image/png')
 * @returns {Promise<string>} The download URL of the uploaded blob
 */
export const uploadBlob = async (blob, path, contentType = 'image/png') => {
  if (!blob || !path) {
    throw new Error('Missing required parameters for blob upload');
  }

  try {
    // Create storage reference with custom path
    const storageRef = ref(storage, path);

    // Upload the blob with metadata
    const metadata = {
      contentType: contentType
    };
    const snapshot = await uploadBytes(storageRef, blob, metadata);

    // Get the download URL
    const downloadURL = await getDownloadURL(snapshot.ref);

    return downloadURL;
  } catch (error) {
    console.error('[PhotoStorage] Blob upload failed:', error);
    throw error;
  }
};

/**
 * Resize an image file to a maximum width/height while maintaining aspect ratio
 * @param {File} file - The image file to resize
 * @param {number} maxSize - Maximum width/height in pixels (default: 800)
 * @returns {Promise<Blob>} The resized image as a Blob
 */
export const resizeImage = async (file, maxSize = 800) => {
  const normalizedFile = await normalizeImageFile(file);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const img = new Image();

      img.onload = () => {
        const canvas = document.createElement('canvas');
        let width = img.width;
        let height = img.height;

        // Calculate new dimensions
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }

        canvas.width = width;
        canvas.height = height;

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Convert to blob with compression
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to resize image'));
            }
          },
          'image/jpeg',
          0.85 // 85% quality
        );
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(normalizedFile);
  });
};

// Export default for backward compatibility
export default {
  uploadPhoto,
  uploadBlob,
  blobToFile,
  isSupportedImageFile,
  normalizeImageFile,
  resizeImage
};
